import { useState, useRef, useEffect } from 'react';
import Cookies from 'js-cookie';
import JSZip from 'jszip';
import JSZipUtils from 'jszip-utils';
import saveAs from 'save-as';
import sanitize from 'sanitize-filename';
import { KEYS } from '../../../constants';

// Document cache to avoid redundant downloads
const documentCache = {};

function useDocumentCart(props) {
  let docsInCart = [];

  const { current: labels } = useRef(JSON.parse(props.labels));
  const [docsBtnString, setDocsBtnString] = useState(labels.myDocuments);
  const [docs, setDocs] = useState(docsInCart);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCartModal, setShowCartModal] = useState(false);
  const [showDocsButton, setShowDocsButton] = useState(false);
  const [showEmailButton, setShowEmailButton] = useState(true);

  useEffect(() => {
    const documentCartBtn = document.querySelector('.document-cart__btn');
    const chatBotElement = document.querySelector('#gaf-genius-widget-root');
    if (!chatBotElement || !documentCartBtn) return;

    if (!showCartModal || !showDocsButton) {
      documentCartBtn.blur();
      chatBotElement.style.transform = 'none';
    } else if (showCartModal) {
      chatBotElement.style.transform = 'translateX(0)';
    }
  }, [showDocsButton, showCartModal]);

  const getDocsInCart = () => {
    //docsInCart = Cookies.get('siplast_docs');
    docsInCart = window.localStorage.getItem('siplast_docs');
    docsInCart ? (docsInCart = JSON.parse(docsInCart)) : (docsInCart = []);

    if (docsInCart?.length >= 1) {
      setShowDocsButton(true);
    } else {
      setShowDocsButton(false);
      setShowCartModal(false);
    }

    if (docsInCart?.length <= 10) {
      // The number of 10 max documents was decided by the client to meet the 2000 character limit.
      setShowEmailButton(true);
      setErrorMessage('');
    } else {
      setShowEmailButton(false);
      //TODO: Show error message and disable button
      setErrorMessage(labels.errorMessageTooManyDocs);
    }

    setDocs(docsInCart);

    setDocsBtnString(`${labels.myDocuments} (${docsInCart?.length})`);
  };

  const handleDocsButtonClick = e => {
    setShowCartModal(true);
  };

  const handleClearAll = e => {
    //Cookies.set('siplast_docs', JSON.stringify([]), { expires: 36500 });
    window.localStorage.removeItem('siplast_docs');
    window.dispatchEvent(new Event('removedfromcart'));
    getDocsInCart();
  };

  const handleCloseClick = e => {
    const isCloseButton = e.currentTarget.classList.contains(
      'modal__close-button'
    );

    const isModalBackdrop = e.target.classList.contains('document-cart');
    if (isCloseButton || isModalBackdrop) {
      setShowCartModal(false);
    }
  };

  const handleDeleteClick = e => {
    const idx = parseInt(e.currentTarget.dataset.idx);

    setDocs(docs.splice(idx, 1));
    //Cookies.set('siplast_docs', JSON.stringify(docs), { expires: 36500 });
    window.localStorage.setItem('siplast_docs', JSON.stringify(docs));
    window.dispatchEvent(new Event('removedfromcart'));
    getDocsInCart();
  };

  const handleEmailClick = e => {
    const modal = document.getElementById('document-cart-modal');
    const closeModal = document.querySelector(
      '#document-cart-modal .modal__close-button'
    );
    const backdrop = document.querySelector(
      '#document-cart-modal .modal__backdrop'
    );
    modal.setAttribute('aria-hidden', 'false');

    closeModal.addEventListener(
      'click',
      () => {
        modal.setAttribute('aria-hidden', 'true');
      },
      false
    );

    backdrop.addEventListener('click', e => {
      if (e.target === backdrop) {
        modal.setAttribute('aria-hidden', 'true');
      }
    });

    const emailTemplate = labels.emailTemplate;
    const replacedEmailTemplate = emailTemplate.replaceAll('\n', '%0D%0A'); // %0D%0A  is the accepted new line character
    let documentList = '';

    docs.forEach($doc => {
      let url = $doc.docUrl;
      url = encodeURIComponent(url);
      documentList += `${url}%0D%0A`;
    });

    const emailContents = replacedEmailTemplate.replace(
      'URL_LIST_TOKEN',
      documentList
    );
    //Use for same page opening
    //window.location.href=`mailto:?body=${emailContents}`;
    //Use for new tab opening
    window.open(`mailto:%20?body=${emailContents}`, '_blank');

    //TODO: Show modal here
  };

  const handleExportClick = e => {
    const zip = new JSZip();
    const zipFilename = labels.zipFilename;
    let count = 0;

    setIsLoading(true);

    docs.forEach($doc => {
      const url = $doc.docUrl;
      const urlArr = url.split('.');
      const extension = urlArr[urlArr.length - 1];

      // Max length of a pathname usually is 256 characters. As all these files are going to be
      // inside a zip file and we don't know where is going to be extracted, I set a limit of
      // 128 characters (half of max length) for each file inside the zip. Plus, as the filename
      // is taken from the document name, I sanitize in case of not allowed characters
      const filename = `${sanitize($doc.docName).substring(
        0,
        124
      )}.${extension}`;

      try {
        // Check if document is already in cache
        if (documentCache[url]) {
          // Use cached document data
          zip.file(filename, documentCache[url], { binary: true });
          count++;

          if (count === docs.length) {
            zip.generateAsync({ type: 'blob' }).then(content => {
              saveAs(content, zipFilename);
            });

            setErrorMessage(null);
            setIsLoading(false);
          }
        } else {
          // Add cache-busting parameter to prevent browser caching issues
          const cacheBustUrl = url.includes('?')
            ? `${url}&_nocache=${new Date().getTime()}`
            : `${url}?_nocache=${new Date().getTime()}`;

          // Fetch document and cache it
          JSZipUtils.getBinaryContent(cacheBustUrl, (err, data) => {
            if (err) {
              setErrorMessage(`${labels.errorMessage} (${err})`);
              setIsLoading(false);
              return;
            }

            // Store in cache for future use
            documentCache[url] = data;
            zip.file(filename, data, { binary: true });
            count++;

            if (count === docs.length) {
              zip.generateAsync({ type: 'blob' }).then(content => {
                saveAs(content, zipFilename);
              });

              setErrorMessage(null);
              setIsLoading(false);
            }
          });
        }
      } catch (err) {
        setErrorMessage(`${labels.errorMessage} (${err})`);
        setIsLoading(false);
      }
    });
  };

  const handleKeyDown = ({ keyCode }) => {
    if (keyCode === KEYS.ESC) {
      const emailModal = document.querySelector(
        '#document-cart-modal[aria-hidden="false"]'
      );
      if (emailModal) {
        emailModal.setAttribute('aria-hidden', 'true');
      } else {
        setShowCartModal(false);
      }
    }
  };

  useEffect(getDocsInCart, []);

  // we use this to avoid having two simultaneous scrollbars
  useEffect(() => {
    const body = document._$('body');
    showCartModal
      ? (body.style.overflow = 'hidden')
      : (body.style.overflow = 'visible');
  }, [showCartModal]);

  useEffect(() => {
    window._on('addtocartclick', getDocsInCart);
    window._on('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('addtocartclick', getDocsInCart);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return {
    docsBtnString,
    docs,
    errorMessage,
    isLoading,
    labels,
    showCartModal,
    showDocsButton,
    showEmailButton,
    handleClearAll,
    handleCloseClick,
    handleDeleteClick,
    handleEmailClick,
    handleExportClick,
    handleDocsButtonClick,
    handleKeyDown,
    setShowCartModal
  };
}

export default useDocumentCart;
